import { useEffect, useState } from "react";
import {
  ADMIN_ACCESS_ITEMS,
  ADMIN_ROLE_OPTIONS,
  INITIAL_ADMIN_USER_FORM,
  createAdminPermissionState,
} from "../assets/instateamMasterConfig";
import {
  changeCurrentAdminPassword,
  createAdminUser,
  deleteAdminUser,
  getCurrentAdmin,
  listAdminUsers,
  updateAdminUserAccess,
  updateAdminUserPassword,
} from "../api/instateamAdminApi";
import { AppIcon } from "../components/InstateamMasterComponents";
import { formatDateTimeLabel, getInitials } from "../utils/adminHelpers";
import { normalizeAdminPermissions } from "../utils/adminPermissions";

const INITIAL_SELF_PASSWORD_FORM = {
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
};

const INITIAL_TARGET_PASSWORD_FORM = {
  adminUserId: "",
  fullName: "",
  newPassword: "",
  confirmPassword: "",
};

export default function AdminProfilePage({
  token,
  sessionAdmin,
  onSessionUpdate,
  onProtectedError,
  onNotify,
}) {
  const [profile, setProfile] = useState(sessionAdmin || null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [selfPasswordForm, setSelfPasswordForm] = useState(INITIAL_SELF_PASSWORD_FORM);
  const [isSavingSelfPassword, setIsSavingSelfPassword] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);
  const [isLoadingAdminUsers, setIsLoadingAdminUsers] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState(INITIAL_ADMIN_USER_FORM);
  const [isCreatingAdmin, setIsCreatingAdmin] = useState(false);
  const [passwordModal, setPasswordModal] = useState(INITIAL_TARGET_PASSWORD_FORM);
  const [isSavingTargetPassword, setIsSavingTargetPassword] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [actionAdminId, setActionAdminId] = useState("");

  const canManageAdminUsers = profile?.canManageAdminUsers || sessionAdmin?.canManageAdminUsers;

  useEffect(() => {
    setProfile(sessionAdmin || null);
  }, [sessionAdmin]);

  useEffect(() => {
    if (!token) return;

    let isActive = true;

    async function hydrateProfilePage() {
      try {
        setIsLoadingProfile(true);
        const payload = await getCurrentAdmin(token);
        if (!isActive) return;

        setProfile(payload.admin);
        onSessionUpdate?.(payload.admin);
      } catch (error) {
        if (!isActive) return;
        onNotify?.({
          type: "error",
          text: error.message,
        });
        onProtectedError?.(error);
      } finally {
        if (isActive) {
          setIsLoadingProfile(false);
        }
      }
    }

    hydrateProfilePage();

    return () => {
      isActive = false;
    };
  }, [token, onNotify, onProtectedError, onSessionUpdate]);

  useEffect(() => {
    if (!token || !canManageAdminUsers) return;

    let isActive = true;

    async function hydrateAdminUsers() {
      try {
        setIsLoadingAdminUsers(true);
        const payload = await listAdminUsers(token);
        if (!isActive) return;
        setAdminUsers(payload.admins || []);
      } catch (error) {
        if (!isActive) return;
        onNotify?.({
          type: "error",
          text: error.message,
        });
        onProtectedError?.(error);
      } finally {
        if (isActive) {
          setIsLoadingAdminUsers(false);
        }
      }
    }

    hydrateAdminUsers();

    return () => {
      isActive = false;
    };
  }, [token, canManageAdminUsers, onNotify, onProtectedError]);

  function updateCreatePermission(key, checked) {
    setCreateForm((current) => ({
      ...current,
      permissions: {
        ...current.permissions,
        [key]: checked,
      },
    }));
  }

  function handleCreateRoleChange(nextRole) {
    setCreateForm((current) => ({
      ...current,
      role: nextRole,
      permissions:
        nextRole === "super_admin"
          ? createAdminPermissionState(true)
          : current.permissions,
    }));
  }

  async function handleSaveOwnPassword(event) {
    event.preventDefault();

    if (
      !selfPasswordForm.currentPassword.trim() ||
      !selfPasswordForm.newPassword.trim() ||
      !selfPasswordForm.confirmPassword.trim()
    ) {
      onNotify?.({
        type: "warning",
        text: "Fill current password, new password, and confirm password first.",
      });
      return;
    }

    if (selfPasswordForm.newPassword !== selfPasswordForm.confirmPassword) {
      onNotify?.({
        type: "warning",
        text: "New password and confirm password must match.",
      });
      return;
    }

    try {
      setIsSavingSelfPassword(true);
      await changeCurrentAdminPassword(token, {
        currentPassword: selfPasswordForm.currentPassword,
        newPassword: selfPasswordForm.newPassword,
      });

      setSelfPasswordForm(INITIAL_SELF_PASSWORD_FORM);
      onNotify?.({
        type: "success",
        text: "Your admin password was updated successfully.",
      });
    } catch (error) {
      onNotify?.({
        type: "error",
        text: error.message,
      });
      onProtectedError?.(error);
    } finally {
      setIsSavingSelfPassword(false);
    }
  }

  async function handleCreateAdminUser(event) {
    event.preventDefault();

    if (
      !createForm.fullName.trim() ||
      !createForm.email.trim() ||
      !createForm.username.trim() ||
      !createForm.password.trim() ||
      !createForm.confirmPassword.trim()
    ) {
      onNotify?.({
        type: "warning",
        text: "Complete the new admin form before saving.",
      });
      return;
    }

    if (createForm.password !== createForm.confirmPassword) {
      onNotify?.({
        type: "warning",
        text: "Password and confirm password must match.",
      });
      return;
    }

    try {
      setIsCreatingAdmin(true);
      const payload = await createAdminUser(token, {
        fullName: createForm.fullName,
        email: createForm.email,
        username: createForm.username,
        password: createForm.password,
        role: createForm.role,
        permissions:
          createForm.role === "super_admin"
            ? createAdminPermissionState(true)
            : createForm.permissions,
      });

      setAdminUsers((current) => [payload.admin, ...current]);
      setCreateForm(INITIAL_ADMIN_USER_FORM);
      setIsCreateModalOpen(false);
      onNotify?.({
        type: "success",
        text: "Admin user created successfully.",
      });
    } catch (error) {
      onNotify?.({
        type: "error",
        text: error.message,
      });
      onProtectedError?.(error);
    } finally {
      setIsCreatingAdmin(false);
    }
  }

  async function handleToggleAdminAccess(adminUser, accessKey) {
    if (!canManageAdminUsers || adminUser.role === "super_admin") return;

    const nextPermissions = {
      ...normalizeAdminPermissions(adminUser.permissions),
      [accessKey]: !adminUser.permissions?.[accessKey],
    };

    try {
      setActionAdminId(`${adminUser._id}:${accessKey}`);
      const payload = await updateAdminUserAccess(token, adminUser._id, {
        role: adminUser.role,
        permissions: nextPermissions,
      });

      setAdminUsers((current) =>
        current.map((item) => (item._id === adminUser._id ? payload.admin : item))
      );

      onNotify?.({
        type: "success",
        text: `${adminUser.fullName}'s access updated.`,
      });
    } catch (error) {
      onNotify?.({
        type: "error",
        text: error.message,
      });
      onProtectedError?.(error);
    } finally {
      setActionAdminId("");
    }
  }

  async function handleSaveTargetPassword(event) {
    event.preventDefault();

    if (
      !passwordModal.adminUserId ||
      !passwordModal.newPassword.trim() ||
      !passwordModal.confirmPassword.trim()
    ) {
      onNotify?.({
        type: "warning",
        text: "Enter and confirm the new password first.",
      });
      return;
    }

    if (passwordModal.newPassword !== passwordModal.confirmPassword) {
      onNotify?.({
        type: "warning",
        text: "Password and confirm password must match.",
      });
      return;
    }

    try {
      setIsSavingTargetPassword(true);
      await updateAdminUserPassword(
        token,
        passwordModal.adminUserId,
        passwordModal.newPassword
      );

      setPasswordModal(INITIAL_TARGET_PASSWORD_FORM);
      onNotify?.({
        type: "success",
        text: "Admin user password updated successfully.",
      });
    } catch (error) {
      onNotify?.({
        type: "error",
        text: error.message,
      });
      onProtectedError?.(error);
    } finally {
      setIsSavingTargetPassword(false);
    }
  }

  async function handleDeleteAdminUser() {
    if (!deleteTarget?._id) return;

    try {
      setActionAdminId(`delete:${deleteTarget._id}`);
      await deleteAdminUser(token, deleteTarget._id);
      setAdminUsers((current) =>
        current.filter((item) => item._id !== deleteTarget._id)
      );
      setDeleteTarget(null);
      onNotify?.({
        type: "success",
        text: "Admin user deleted successfully.",
      });
    } catch (error) {
      onNotify?.({
        type: "error",
        text: error.message,
      });
      onProtectedError?.(error);
    } finally {
      setActionAdminId("");
    }
  }

  const safeProfile = profile || sessionAdmin;

  return (
    <>
      <section className="admin-profile-page">
        <article className="admin-profile-hero-card">
          <div className="admin-profile-avatar">{getInitials(safeProfile?.fullName)}</div>
          <div className="admin-profile-hero-copy">
            <div className="admin-profile-heading-row">
              <div>
                <h1>{safeProfile?.fullName || "Admin User"}</h1>
                <p>{safeProfile?.roleLabel || "Admin"}</p>
              </div>
              <span className="admin-profile-badge">
                {safeProfile?.canManageAdminUsers ? "Super Admin" : "Admin Account"}
              </span>
            </div>

            <div className="admin-profile-meta-grid">
              <div>
                <span>Email</span>
                <strong>{safeProfile?.email || "—"}</strong>
              </div>
              <div>
                <span>Username</span>
                <strong>{safeProfile?.username || "—"}</strong>
              </div>
              <div>
                <span>Last Login</span>
                <strong>{formatDateTimeLabel(safeProfile?.lastLoginAt)}</strong>
              </div>
              <div>
                <span>Joined</span>
                <strong>{formatDateTimeLabel(safeProfile?.createdAt)}</strong>
              </div>
            </div>
          </div>
        </article>

        <div className="admin-profile-grid">
          <article className="admin-profile-card">
            <div className="admin-profile-card-head">
              <div>
                <h3>Admin Account</h3>
                <p>View your current admin account details.</p>
              </div>
              {isLoadingProfile ? <span className="admin-inline-status">Refreshing...</span> : null}
            </div>

            <div className="admin-profile-info-grid">
              <div>
                <span>Full Name</span>
                <strong>{safeProfile?.fullName || "—"}</strong>
              </div>
              <div>
                <span>Email Address</span>
                <strong>{safeProfile?.email || "—"}</strong>
              </div>
              <div>
                <span>Username</span>
                <strong>{safeProfile?.username || "—"}</strong>
              </div>
              <div>
                <span>Role</span>
                <strong>{safeProfile?.roleLabel || "Admin"}</strong>
              </div>
            </div>
          </article>

          <article className="admin-profile-card">
            <div className="admin-profile-card-head">
              <div>
                <h3>Change Password</h3>
                <p>Update your admin login password securely.</p>
              </div>
            </div>

            <form className="admin-profile-password-form" onSubmit={handleSaveOwnPassword}>
              <label className="form-field">
                <span>Current Password</span>
                <div className="field-shell">
                  <AppIcon name="lock" />
                  <input
                    type="password"
                    value={selfPasswordForm.currentPassword}
                    onChange={(event) =>
                      setSelfPasswordForm((current) => ({
                        ...current,
                        currentPassword: event.target.value,
                      }))
                    }
                    placeholder="Enter current password"
                  />
                </div>
              </label>

              <label className="form-field">
                <span>New Password</span>
                <div className="field-shell">
                  <AppIcon name="lock" />
                  <input
                    type="password"
                    value={selfPasswordForm.newPassword}
                    onChange={(event) =>
                      setSelfPasswordForm((current) => ({
                        ...current,
                        newPassword: event.target.value,
                      }))
                    }
                    placeholder="Enter new password"
                  />
                </div>
              </label>

              <label className="form-field">
                <span>Confirm Password</span>
                <div className="field-shell">
                  <AppIcon name="lock" />
                  <input
                    type="password"
                    value={selfPasswordForm.confirmPassword}
                    onChange={(event) =>
                      setSelfPasswordForm((current) => ({
                        ...current,
                        confirmPassword: event.target.value,
                      }))
                    }
                    placeholder="Confirm new password"
                  />
                </div>
              </label>

              <div className="admin-profile-password-actions">
                <button className="primary-button" type="submit">
                  {isSavingSelfPassword ? "Updating..." : "Change Password"}
                </button>
              </div>
            </form>
          </article>
        </div>

        {canManageAdminUsers ? (
          <section className="admin-users-panel">
            <div className="admin-users-panel-head">
              <div>
                <h2>Admin Users</h2>
                <p>Create admin accounts, reset passwords, and control page access.</p>
              </div>
              <button
                className="primary-button"
                type="button"
                onClick={() => setIsCreateModalOpen(true)}
              >
                <AppIcon name="userPlus" /> Add Admin User
              </button>
            </div>

            <div className="admin-users-list">
              {isLoadingAdminUsers ? (
                <div className="admin-users-empty">Loading admin users...</div>
              ) : adminUsers.length ? (
                adminUsers.map((adminUser) => (
                  <article className="admin-user-card" key={adminUser._id}>
                    <div className="admin-user-card-head">
                      <div className="admin-user-identity">
                        <div className="admin-user-avatar">
                          {getInitials(adminUser.fullName)}
                        </div>
                        <div>
                          <div className="admin-user-title-row">
                            <h3>{adminUser.fullName}</h3>
                            <span className="admin-user-role-badge">
                              {adminUser.roleLabel}
                            </span>
                            {adminUser.isCurrentUser ? (
                              <span className="admin-user-current-badge">You</span>
                            ) : null}
                          </div>
                          <p>
                            @{adminUser.username} • {adminUser.email}
                          </p>
                        </div>
                      </div>

                      <div className="admin-user-card-actions">
                        <button
                          className="secondary-button"
                          type="button"
                          onClick={() =>
                            setPasswordModal({
                              adminUserId: adminUser._id,
                              fullName: adminUser.fullName,
                              newPassword: "",
                              confirmPassword: "",
                            })
                          }
                        >
                          Change Password
                        </button>
                        {!adminUser.isCurrentUser ? (
                          <button
                            className="secondary-button admin-danger-button"
                            type="button"
                            onClick={() => setDeleteTarget(adminUser)}
                          >
                            Delete
                          </button>
                        ) : null}
                      </div>
                    </div>

                    <div className="admin-user-meta-grid">
                      <div>
                        <span>Role</span>
                        <strong>{adminUser.roleLabel}</strong>
                      </div>
                      <div>
                        <span>Last Login</span>
                        <strong>{formatDateTimeLabel(adminUser.lastLoginAt)}</strong>
                      </div>
                      <div>
                        <span>Created</span>
                        <strong>{formatDateTimeLabel(adminUser.createdAt)}</strong>
                      </div>
                    </div>

                    <div className="admin-access-section">
                      <div className="admin-access-head">
                        <strong>Screen Access</strong>
                        <span>
                          {adminUser.role === "super_admin"
                            ? "Super admin keeps all permissions enabled."
                            : "Toggle which sidebar tabs and notifications this admin can access."}
                        </span>
                      </div>

                      <div className="admin-access-grid">
                        {ADMIN_ACCESS_ITEMS.map((item) => {
                          const isActive = Boolean(adminUser.permissions?.[item.key]);
                          const isBusy =
                            actionAdminId === `${adminUser._id}:${item.key}`;

                          return (
                            <button
                              key={`${adminUser._id}:${item.key}`}
                              type="button"
                              className={`admin-access-toggle ${isActive ? "active" : ""} ${
                                adminUser.role === "super_admin" ? "disabled" : ""
                              }`}
                              onClick={() => handleToggleAdminAccess(adminUser, item.key)}
                              disabled={adminUser.role === "super_admin" || isBusy}
                            >
                              <span>{item.label}</span>
                              <strong>{isBusy ? "..." : isActive ? "On" : "Off"}</strong>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </article>
                ))
              ) : (
                <div className="admin-users-empty">
                  No additional admin users have been created yet.
                </div>
              )}
            </div>
          </section>
        ) : (
          <section className="admin-users-panel admin-users-panel-note">
            <div className="admin-users-empty">
              Only the super admin account can create and manage other admin users.
            </div>
          </section>
        )}
      </section>

      {isCreateModalOpen ? (
        <div className="modal-overlay" role="presentation" onClick={() => setIsCreateModalOpen(false)}>
          <section
            className="employee-modal admin-user-modal"
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="employee-modal-title">
              <h3>Create Admin User</h3>
              <p>Create a new admin login and assign screen access.</p>
            </div>

            <form className="admin-user-create-form" onSubmit={handleCreateAdminUser}>
              <div className="employee-modal-grid admin-user-create-grid">
                <label className="form-field">
                  <span>Full Name</span>
                  <div className="field-shell">
                    <AppIcon name="employees" />
                    <input
                      type="text"
                      value={createForm.fullName}
                      onChange={(event) =>
                        setCreateForm((current) => ({
                          ...current,
                          fullName: event.target.value,
                        }))
                      }
                      placeholder="Enter full name"
                    />
                  </div>
                </label>

                <label className="form-field">
                  <span>Email Address</span>
                  <div className="field-shell">
                    <AppIcon name="mail" />
                    <input
                      type="email"
                      value={createForm.email}
                      onChange={(event) =>
                        setCreateForm((current) => ({
                          ...current,
                          email: event.target.value,
                        }))
                      }
                      placeholder=""
                    />
                  </div>
                </label>

                <label className="form-field">
                  <span>Username</span>
                  <div className="field-shell">
                    <AppIcon name="employees" />
                    <input
                      type="text"
                      value={createForm.username}
                      onChange={(event) =>
                        setCreateForm((current) => ({
                          ...current,
                          username: event.target.value,
                        }))
                      }
                      placeholder="Enter username"
                    />
                  </div>
                </label>

                <label className="form-field">
                  <span>Role</span>
                  <div className="field-shell select-shell">
                    <select
                      value={createForm.role}
                      onChange={(event) => handleCreateRoleChange(event.target.value)}
                    >
                      {ADMIN_ROLE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <AppIcon name="chevronDown" />
                  </div>
                </label>

                <label className="form-field">
                  <span>Password</span>
                  <div className="field-shell">
                    <AppIcon name="lock" />
                    <input
                      type="password"
                      value={createForm.password}
                      onChange={(event) =>
                        setCreateForm((current) => ({
                          ...current,
                          password: event.target.value,
                        }))
                      }
                      placeholder="Enter password"
                    />
                  </div>
                </label>

                <label className="form-field">
                  <span>Confirm Password</span>
                  <div className="field-shell">
                    <AppIcon name="lock" />
                    <input
                      type="password"
                      value={createForm.confirmPassword}
                      onChange={(event) =>
                        setCreateForm((current) => ({
                          ...current,
                          confirmPassword: event.target.value,
                        }))
                      }
                      placeholder="Confirm password"
                    />
                  </div>
                </label>
              </div>

              <div className="admin-access-section admin-access-section-compact">
                <div className="admin-access-head">
                  <strong>Access Controls</strong>
                  <span>
                    Toggle the screens this admin can access after login.
                  </span>
                </div>

                <div className="admin-access-grid">
                  {ADMIN_ACCESS_ITEMS.map((item) => (
                    <button
                      key={`create-${item.key}`}
                      type="button"
                      className={`admin-access-toggle ${
                        createForm.permissions[item.key] ? "active" : ""
                      } ${createForm.role === "super_admin" ? "disabled" : ""}`}
                      onClick={() =>
                        createForm.role === "super_admin"
                          ? null
                          : updateCreatePermission(
                              item.key,
                              !createForm.permissions[item.key]
                            )
                      }
                      disabled={createForm.role === "super_admin"}
                    >
                      <span>{item.label}</span>
                      <strong>{createForm.permissions[item.key] ? "On" : "Off"}</strong>
                    </button>
                  ))}
                </div>
              </div>

              <div className="employee-form-actions">
                <button
                  className="secondary-button"
                  type="button"
                  onClick={() => {
                    setCreateForm(INITIAL_ADMIN_USER_FORM);
                    setIsCreateModalOpen(false);
                  }}
                >
                  Cancel
                </button>
                <button className="primary-button" type="submit">
                  {isCreatingAdmin ? "Creating..." : "Create Admin"}
                </button>
              </div>
            </form>
          </section>
        </div>
      ) : null}

      {passwordModal.adminUserId ? (
        <div className="modal-overlay" role="presentation" onClick={() => setPasswordModal(INITIAL_TARGET_PASSWORD_FORM)}>
          <section
            className="employee-modal admin-user-modal small"
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="employee-modal-title">
              <h3>Change Admin Password</h3>
              <p>Set a new login password for {passwordModal.fullName}.</p>
            </div>

            <form className="admin-user-password-form" onSubmit={handleSaveTargetPassword}>
              <label className="form-field">
                <span>New Password</span>
                <div className="field-shell">
                  <AppIcon name="lock" />
                  <input
                    type="password"
                    value={passwordModal.newPassword}
                    onChange={(event) =>
                      setPasswordModal((current) => ({
                        ...current,
                        newPassword: event.target.value,
                      }))
                    }
                    placeholder="Enter new password"
                  />
                </div>
              </label>

              <label className="form-field">
                <span>Confirm Password</span>
                <div className="field-shell">
                  <AppIcon name="lock" />
                  <input
                    type="password"
                    value={passwordModal.confirmPassword}
                    onChange={(event) =>
                      setPasswordModal((current) => ({
                        ...current,
                        confirmPassword: event.target.value,
                      }))
                    }
                    placeholder="Confirm new password"
                  />
                </div>
              </label>

              <div className="employee-form-actions">
                <button
                  className="secondary-button"
                  type="button"
                  onClick={() => setPasswordModal(INITIAL_TARGET_PASSWORD_FORM)}
                >
                  Cancel
                </button>
                <button className="primary-button" type="submit">
                  {isSavingTargetPassword ? "Saving..." : "Update Password"}
                </button>
              </div>
            </form>
          </section>
        </div>
      ) : null}

      {deleteTarget ? (
        <div className="modal-overlay" role="presentation" onClick={() => setDeleteTarget(null)}>
          <section
            className="employee-modal logout-confirm-modal"
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="employee-modal-title">
              <h3>Delete Admin User?</h3>
              <p>
                Are you sure you want to delete {deleteTarget.fullName}? This admin will no longer
                be able to login.
              </p>
            </div>

            <div className="logout-confirm-actions">
              <button className="secondary-button" type="button" onClick={() => setDeleteTarget(null)}>
                Cancel
              </button>
              <button className="primary-button admin-danger-solid" type="button" onClick={handleDeleteAdminUser}>
                {actionAdminId === `delete:${deleteTarget._id}` ? "Deleting..." : "Delete Admin"}
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </>
  );
}
